import React, { useState, useEffect } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import Paywall from "./Paywall";
import Onboarding, { isOnboarded, getOnboardingData } from "./Onboarding";
import PlanPreview from "./PlanPreview";
import { Loader2 } from "lucide-react";

const PREVIEW_SEEN_KEY = "fff_preview_seen";
const PAYWALL_TEST_KEY = "fff-paywall-test-mode";

/** True when the tester is force-testing the paywall via ?paywall=1.
 * Reads BOTH the URL (to catch the initial navigation) and sessionStorage
 * (to persist across in-tab navigation).
 */
const isPaywallTestMode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("paywall")) {
      const v = params.get("paywall");
      if (v === "0" || v === "off" || v === "false") {
        sessionStorage.removeItem(PAYWALL_TEST_KEY);
        return false;
      }
      sessionStorage.setItem(PAYWALL_TEST_KEY, "1");
      return true;
    }
    return sessionStorage.getItem(PAYWALL_TEST_KEY) === "1";
  } catch { return false; }
};

export default function RequirePro({ children }) {
  const { loading, isPro, refresh } = useSubscription();
  const [onboarded, setOnboarded] = useState(isOnboarded());
  const [previewSeen, setPreviewSeen] = useState(localStorage.getItem(PREVIEW_SEEN_KEY) === "1");
  // Force-render the paywall when ?paywall=1 is in the URL, regardless of
  // whatever SubscriptionContext eventually decides. This ensures testers on
  // pre-existing Pro sessions can still see the paywall without waiting for
  // the async refresh() to override isPro.
  const paywallForced = isPaywallTestMode();

  useEffect(() => { setOnboarded(isOnboarded()); }, []);

  if (loading && !paywallForced) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] grid place-items-center" data-testid="pro-loading">
        <Loader2 className="h-6 w-6 text-lime-400 animate-spin" />
      </div>
    );
  }

  if (!onboarded) {
    return <Onboarding onComplete={() => setOnboarded(true)} />;
  }

  if (paywallForced) {
    return <Paywall onClose={refresh} />;
  }

  if (!isPro && !previewSeen) {
    return (
      <PlanPreview
        data={getOnboardingData()}
        onContinue={() => {
          localStorage.setItem(PREVIEW_SEEN_KEY, "1");
          setPreviewSeen(true);
        }}
      />
    );
  }

  if (!isPro) {
    return <Paywall onClose={refresh} />;
  }

  return children;
}
